import clsx from 'clsx';
import { getMonth } from 'date-fns';
import React, { ReactElement, useState } from 'react';
import Calendar, { CalendarProps } from 'react-calendar';

import { Icon } from '../icon/Icon';
import { Tooltip, TooltipTrigger } from '../tooltip/Tooltip';
import styles from './DatePicker.module.scss';

const CUSTOM_DISABLED_CLASS_NAME = 'react-calendar__tile--disabled';
const MONTH_ACTIVE_TILE = 'react-calendar__tile--hasActive';

interface NavigationTooltipFactoryProp {
  label: string;
  children: ReactElement | string;
}

export type DateValue = Date | null;

enum CalendarView {
  Month = 'month',
  Year = 'year',
  Decade = 'decade',
  Century = 'century',
}

export type DatePickerValue = DateValue | [DateValue, DateValue];

export interface DatePickerLocales {
  selectYear: string;
  selectMonth: string;
  selectDecade: string;
  previousMonth: string;
  previousYear: string;
  previousDecade: string;
  nextMonth: string;
  nextYear: string;
  nextDecade: string;
}

export interface DatePickerProp {
  value: DatePickerValue;
  selectRange?: boolean;
  maxDate?: Date;
  minDate?: Date;
  onDateChange: (newDate: DatePickerValue) => void;
  locales: DatePickerLocales;
  tileDisabled?: (props: { date: Date; view: string }) => boolean;
}

export const DatePicker = ({
  value,
  selectRange = false,
  maxDate,
  minDate,
  onDateChange,
  locales,
  tileDisabled,
  ...rest
}: DatePickerProp) => {
  const [currentView, setCurrentView] = useState<CalendarProps['view']>(
    CalendarView.Month
  );

  const isInMonthView = currentView === CalendarView.Month;
  const isDecadeView = currentView === CalendarView.Decade;

  const navigationTooltipFactory = ({
    label,
    children,
  }: NavigationTooltipFactoryProp) => {
    return (
      <Tooltip
        isTouchDevice={false}
        label={label}
        strategy="fixed"
        trigger={TooltipTrigger.Hover}
        inPortal
      >
        <div>{children}</div>
      </Tooltip>
    );
  };

  const getSelectLabel = () => {
    switch (currentView) {
      case CalendarView.Year:
        return locales.selectYear;
      case CalendarView.Month:
        return locales.selectMonth;
      case CalendarView.Decade:
        return locales.selectDecade;
      default:
        return '';
    }
  };

  const getNextLabel = () => {
    switch (currentView) {
      case CalendarView.Year:
        return locales.nextYear;
      case CalendarView.Month:
        return locales.nextMonth;
      case CalendarView.Decade:
        return locales.nextDecade;
      default:
        return '';
    }
  };

  const getPreviousLabel = () => {
    switch (currentView) {
      case CalendarView.Year:
        return locales.previousYear;
      case CalendarView.Month:
        return locales.previousMonth;
      case CalendarView.Decade:
        return locales.previousDecade;
      default:
        return '';
    }
  };

  return (
    <div className={styles.datePickerWrapper} {...rest}>
      <Calendar
        className={clsx(styles.customDatePicker, {
          [styles.showNavLabel]:
            currentView === CalendarView.Month ||
            currentView === CalendarView.Year,
          [styles.selectRange]: isInMonthView && selectRange,
        })}
        value={value}
        selectRange={isInMonthView && selectRange}
        maxDate={maxDate}
        minDate={minDate}
        showNeighboringDecade
        minDetail={CalendarView.Decade}
        locale={navigator.language}
        formatDay={(locale, date) => {
          const day = date.getDate();
          return new Intl.NumberFormat(locale).format(day);
        }}
        tileDisabled={tileDisabled}
        tileClassName={({ date, view }) => {
          const classNames = [];
          const maxDateCondition =
            maxDate && new Date(date).getTime() > maxDate.getTime();
          const minDateCondition =
            minDate && new Date(date).getTime() < minDate.getTime();
          const customDisabled = tileDisabled && tileDisabled({ date, view });
          const shouldDisable =
            maxDateCondition || minDateCondition || customDisabled;

          if (shouldDisable) {
            classNames.push(CUSTOM_DISABLED_CLASS_NAME);
          }

          if (view === CalendarView.Year && value) {
            const monthOnCalendar = getMonth(date);
            const selectedMonth = getMonth(
              Array.isArray(value) ? (value[0] as Date) : value
            );

            if (monthOnCalendar === selectedMonth) {
              classNames.push(MONTH_ACTIVE_TILE);
            }
          }

          return classNames;
        }}
        formatMonth={(locale, date) => {
          return new Intl.DateTimeFormat(locale, { month: 'short' }).format(
            date
          );
        }}
        navigationLabel={({ label, view }) => {
          return view === CalendarView.Decade
            ? undefined
            : navigationTooltipFactory({
                label: getSelectLabel(),
                children: label,
              });
        }}
        nextLabel={
          !isDecadeView
            ? navigationTooltipFactory({
                label: getNextLabel(),
                children: <Icon iconName="ChevronLightRight" size={12} />,
              })
            : null
        }
        next2Label={
          isInMonthView
            ? navigationTooltipFactory({
                label: locales.nextYear,
                children: <Icon iconName="AngleLightRight" size={12} />,
              })
            : null
        }
        prevLabel={
          !isDecadeView
            ? navigationTooltipFactory({
                label: getPreviousLabel(),
                children: <Icon iconName="ChevronLightLeft" size={12} />,
              })
            : null
        }
        prev2Label={
          isInMonthView
            ? navigationTooltipFactory({
                label: locales.previousYear,
                children: <Icon iconName="AngleLightLeft" size={12} />,
              })
            : null
        }
        onChange={onDateChange}
        onViewChange={({ view }) => {
          setCurrentView(view);
        }}
      />
    </div>
  );
};
